import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";
import { withSelect } from "@wordpress/data";
import { Fragment } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React, { useEffect } from "react";
import IconRenderer from "../../components/common/IconRenderer";
import { useBlockStyle } from "../../hooks";
import "../assets/sass/blocks/grid-module/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "../../utils";
import { Icon } from "../../components";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		posts,
		attributes: {
			icon,
			label,
			hideOnDesktop,
			enableArrows,
			category,
			postCount,
			tickerEffect,
			tickerDirection,
			delayTimer,
			autoPlay,
			scrollSpeed,
			stopOnHover,
			enableIcon,
			enableLabel,
		},
		setAttributes,
		categories,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "news-ticker",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	useEffect(() => {
		const ticker = document.querySelector(`.mzb-news-ticker-${clientId}`);
		if (!ticker) return;

		// Mark as not initialized initially
		ticker.setAttribute("data-initialized", "false");

		const items = ticker.querySelectorAll(".mzb-news-ticker-list li");
		const totalPosts = items.length;

		if (totalPosts === 0) return;

		let currentIndex = 0;
		let intervalId: number | null = null;
		let isPlaying = autoPlay;
		let animationFrameId: number | null = null;

		// Initialize ticker based on effect
		const initializeTicker = () => {
			// Ensure we have items to work with
			if (totalPosts === 0) return;

			// First, hide ALL items completely
			items.forEach((item, idx) => {
				const element = item as HTMLElement;
				element.style.display = "none";
				element.style.position = "relative";
				element.style.left = "0";
				element.style.top = "0";
				element.style.opacity = "0";
				element.style.transform = "translateX(0)";
				element.style.transition = "none";
				element.style.visibility = "hidden";
				element.style.zIndex = "-1";
			});

			// Then show ONLY the current item
			const currentItem = items[currentIndex] as HTMLElement;
			currentItem.style.display = "block";
			currentItem.style.opacity = "1";
			currentItem.style.visibility = "visible";
			currentItem.style.zIndex = "1";

			// Mark as initialized after setup
			ticker.setAttribute("data-initialized", "true");

			if (isPlaying) {
				startAutoPlay();
			}
		};

		// Auto play for all effects
		const startAutoPlay = () => {
			intervalId = setInterval(() => {
				if (isPlaying) {
					nextItem();
				}
			}, delayTimer);
		};

		// Show next item with animation
		const nextItem = () => {
			const currentItem = items[currentIndex] as HTMLElement;
			currentIndex = (currentIndex + 1) % totalPosts;
			const nextItem = items[currentIndex] as HTMLElement;

			animateTransition(currentItem, nextItem);
		};

		// Show previous item with animation
		const prevItem = () => {
			const currentItem = items[currentIndex] as HTMLElement;
			currentIndex =
				currentIndex === 0 ? totalPosts - 1 : currentIndex - 1;
			const prevItemElement = items[currentIndex] as HTMLElement;

			animateTransition(currentItem, prevItemElement);
		};

		// Animate transition between items
		const animateTransition = (
			fromItem: HTMLElement,
			toItem: HTMLElement
		) => {
			// Calculate animation duration based on scrollSpeed (1-100)
			// Higher scrollSpeed = faster animation, lower = slower
			const baseDuration = 400; // Base duration in ms
			const speedMultiplier = Math.max(0.1, (100 - scrollSpeed) / 100); // Invert so higher speed = faster
			const animationDuration = Math.round(
				baseDuration * speedMultiplier
			);

			// Set transition duration based on calculated speed
			const transitionDuration = `${animationDuration}ms`;
			fromItem.style.transition = `opacity ${transitionDuration} ease-in-out, transform ${transitionDuration} ease-in-out`;
			toItem.style.transition = `opacity ${transitionDuration} ease-in-out, transform ${transitionDuration} ease-in-out`;

			switch (tickerEffect) {
				case "fade":
					fromItem.style.opacity = "0";
					setTimeout(() => {
						fromItem.style.display = "none";
						fromItem.style.opacity = "0";
						fromItem.style.transform = "translateX(0)";
						fromItem.style.visibility = "hidden";
						fromItem.style.zIndex = "-1";

						toItem.style.display = "block";
						toItem.style.opacity = "0";
						toItem.style.visibility = "visible";
						toItem.style.zIndex = "1";
						setTimeout(() => {
							toItem.style.opacity = "1";
						}, 50);
					}, animationDuration);
					break;

				case "slide-left":
					fromItem.style.transform = "translateX(-100%)";
					fromItem.style.opacity = "0";
					setTimeout(() => {
						fromItem.style.display = "none";
						fromItem.style.transform = "translateX(0)";
						fromItem.style.opacity = "0";
						fromItem.style.visibility = "hidden";
						fromItem.style.zIndex = "-1";

						toItem.style.display = "block";
						toItem.style.transform = "translateX(100%)";
						toItem.style.opacity = "0";
						toItem.style.visibility = "visible";
						toItem.style.zIndex = "1";
						setTimeout(() => {
							toItem.style.transform = "translateX(0)";
							toItem.style.opacity = "1";
						}, 50);
					}, animationDuration);
					break;

				case "slide-right":
					fromItem.style.transform = "translateX(100%)";
					fromItem.style.opacity = "0";
					setTimeout(() => {
						fromItem.style.display = "none";
						fromItem.style.transform = "translateX(0)";
						fromItem.style.opacity = "0";
						fromItem.style.visibility = "hidden";
						fromItem.style.zIndex = "-1";

						toItem.style.display = "block";
						toItem.style.transform = "translateX(-100%)";
						toItem.style.opacity = "0";
						toItem.style.visibility = "visible";
						toItem.style.zIndex = "1";
						setTimeout(() => {
							toItem.style.transform = "translateX(0)";
							toItem.style.opacity = "1";
						}, 50);
					}, animationDuration);
					break;

				case "none":
					fromItem.style.display = "none";
					fromItem.style.visibility = "hidden";
					fromItem.style.zIndex = "-1";
					toItem.style.display = "block";
					toItem.style.visibility = "visible";
					toItem.style.zIndex = "1";
					break;

				default:
					fromItem.style.display = "none";
					fromItem.style.visibility = "hidden";
					fromItem.style.zIndex = "-1";
					toItem.style.display = "block";
					toItem.style.visibility = "visible";
					toItem.style.zIndex = "1";
			}
		};

		// Handle navigation clicks
		const handleNavClick = (e: Event) => {
			const target = e.target as HTMLElement;
			const button = target.closest(".mzb-news-ticker-nav-btn");
			if (!button) return;

			e.preventDefault();
			const action = button.getAttribute("data-action");

			if (action === "prev") {
				prevItem();
			} else if (action === "next") {
				nextItem();
			}
		};

		// Handle hover events for stop on hover
		const handleMouseEnter = () => {
			if (stopOnHover) {
				isPlaying = false;
				if (intervalId) {
					clearInterval(intervalId);
					intervalId = null;
				}
				if (animationFrameId) {
					cancelAnimationFrame(animationFrameId);
					animationFrameId = null;
				}
			}
		};

		const handleMouseLeave = () => {
			if (stopOnHover && autoPlay) {
				isPlaying = true;
				startAutoPlay();
			}
		};

		// Add event listeners
		ticker.addEventListener("click", handleNavClick);
		ticker.addEventListener("mouseenter", handleMouseEnter);
		ticker.addEventListener("mouseleave", handleMouseLeave);

		// Delay initialization slightly to ensure DOM is ready
		const initTimer = setTimeout(() => {
			initializeTicker();
		}, 50);

		// Cleanup
		return () => {
			clearTimeout(initTimer);
			ticker.removeEventListener("click", handleNavClick);
			ticker.removeEventListener("mouseenter", handleMouseEnter);
			ticker.removeEventListener("mouseleave", handleMouseLeave);

			if (intervalId) {
				clearInterval(intervalId);
			}
			if (animationFrameId) {
				cancelAnimationFrame(animationFrameId);
			}

			// Ensure all items are hidden on cleanup
			items.forEach((item) => {
				const element = item as HTMLElement;
				element.style.display = "none";
				element.style.opacity = "0";
				element.style.visibility = "hidden";
				element.style.zIndex = "-1";
			});

			// Mark as not initialized
			ticker.setAttribute("data-initialized", "false");
		};
	}, [
		clientId,
		posts,
		enableArrows,
		tickerEffect,
		tickerDirection,
		delayTimer,
		autoPlay,
		scrollSpeed,
		stopOnHover,
	]);

	const classNames = classnames(
		`mzb-news-ticker mzb-news-ticker-${clientId}`,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div
				className={classNames}
				data-ticker-effect={tickerEffect}
				data-ticker-direction={tickerDirection}
				data-delay-timer={delayTimer}
				data-auto-play={autoPlay}
				data-scroll-speed={scrollSpeed}
				data-stop-on-hover={stopOnHover}
			>
				{enableIcon && (
					<span className="mzb-weather">
						<IconRenderer
							type="newsTickerIcon"
							name={icon}
							size={24}
						/>
					</span>
				)}
				{enableLabel && <span className="mzb-heading">{label}</span>}
				<div
					className={cn(
						`mzb-news-ticker-box`,
						enableArrows && "mzb-news-ticker-arrows"
					)}
					data-total-posts={posts?.length || 0}
				>
					{enableArrows && (
						<a
							href="#"
							className="mzb-news-ticker-nav-btn prev"
							data-action="prev"
							aria-label="Previous"
						>
							<Icon name="chevronLeft" />
						</a>
					)}
					<ul className={`mzb-news-ticker-list`}>
						{(posts || []).map((post, idx) => {
							// Limit title to 60 characters
							const truncatedTitle =
								post.title.rendered.length > 60
									? post.title.rendered.substring(0, 60) +
									  "..."
									: post.title.rendered;

							return (
								<li key={idx}>
									<a>{truncatedTitle}</a>
								</li>
							);
						})}
					</ul>
					{enableArrows && (
						<a
							href="#"
							className="mzb-news-ticker-nav-btn next"
							data-action="next"
							aria-label="Next"
						>
							<Icon name="chevronRight" />
						</a>
					)}
				</div>
			</div>
		</Fragment>
	);
};

//@ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const {
		attributes: { category, order, postCount },
	} = props;
	const query = {
		order,
	};
	return {
		posts: getEntityRecords("postType", "post", {
			...query,
			per_page: postCount || 4,
			categories: "all" === category ? undefined : parseInt(category),
		}),
		numberOfPosts:
			getEntityRecords("postType", "post", {
				per_page: -1,
				categories: "all" === category ? undefined : parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
	};
})(Edit);
